/*
  Загрузка сведений о помещениях файлом - пункт 3.8 плана.

  Зачем это здесь. Площадь, количество комнат и этаж печатает финансово-лицевой
  счёт - лист, который идёт в суд приложением к заявлению (форма принята юристом
  18.09.2026). Внести их можно и на карточке счёта руками, но по одному счёту, а
  в портфеле якорного клиента их тысячи.

  Памятка «где это взять» стоит НА экране, а не в инструкции сбоку - по той же
  причине, что у выгрузки 1С и идентификаторов: 16.09.2026 специалист заказчика
  не нашёл нужный отчёт в своей же базе и сказал, что такого в программе нет.

  Два обращения на загрузку: сперва «какому счёту что ляжет», потом запись по
  подтверждению.

  🔴 С 20.10.2026 файл уходит на сервер ОДИН раз и разбирается один раз
  (решение владельца 19.10.2026, `useUploadFlow`): «Посмотреть» и запись
  называют уже прочитанный файл номером, запись идёт фоновой работой с ходом,
  конец - блок «Готово»; перечень изменений остаётся под ним.
*/

import { useState } from 'react'

import { api } from '../api/client.js'
import { rows as rowsCount } from '../api/format.js'
import { Notice } from './Common.jsx'
import UploadEnd from './upload/UploadEnd.jsx'
import UploadStages from './upload/UploadStages.jsx'
import useUploadFlow from './upload/useUploadFlow.js'
import UploadHelp from './UploadHelp.jsx'

/*
  Образец файла - он же картинка над кнопкой «Скачать образец».

  Строки взяты из настоящей выгрузки заказчика (ООО «УПК «Ресурс», разбор копии
  базы 18.09.2026): так человек видит не выдуманный, а тот самый вид, который
  получится у него. Вторая строка намеренно неполная - показывает, что пустая
  графа допустима.
*/
const SAMPLE = {
  file: 'помещения-образец.csv',
  headers: ['Лицевой счёт', 'Общая площадь', 'Жилая площадь', 'Комнат', 'Этаж'],
  rows: [
    ['20346', '55,9', '30,3', '2', '9'],
    ['11852', '45,4', '', '2', '4'],
  ],
}

const SHOWN_ROWS = 20
/* Сколько строк показать. Полное число стоит рядом: список в тысячу строк не
   читается, а двадцати хватает, чтобы понять, что не так с файлом. */

function Plan({ plan }) {
  return (
    <div className="onec__summary">
      <p>
        Строк в файле: <b>{rowsCount(plan.total)}</b>. Будет внесено: <b>{plan.written}</b>.{' '}
        {plan.unchanged > 0 ? <>Уже совпадает: {plan.unchanged}.</> : null}
      </p>
      {plan.changes.slice(0, SHOWN_ROWS).map((change) => (
        <p key={change.account_id}>
          Счёт {change.account_number}, {change.address}: {change.fields.join(', ')}.
        </p>
      ))}
      {plan.issues.length > 0 ? (
        <>
          <p className="onec__skipped">
            Требуют внимания: {rowsCount(plan.issues.length)}. {plan.issues.length === 1 ? 'Она не записана.' : 'Они не записаны.'}
          </p>
          {plan.issues.slice(0, SHOWN_ROWS).map((issue) => (
            <p key={`${issue.row_number}-${issue.account_number ?? ''}`} className="onec__skipped">
              Строка {issue.row_number}: {issue.explanation}
            </p>
          ))}
        </>
      ) : null}
    </div>
  )
}

export default function PremisesUpload({ onLoaded }) {
  const flow = useUploadFlow('premises')
  const file = flow.stored
  const [plan, setPlan] = useState(null)
  const [written, setWritten] = useState(false)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)
  // Номер захода: новый заход пересоздаёт поле выбора без имени прежнего файла.
  const [round, setRound] = useState(0)

  function choose(chosen) {
    setPlan(null)
    setWritten(false)
    setError(null)
    flow.choose(chosen ?? null)
  }

  function again() {
    choose(null)
    setRound(round + 1)
  }

  async function act(action, then) {
    setBusy(true)
    setError(null)
    try {
      then(await action())
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  const look = () =>
    act(
      () =>
        flow.check(
          () => api.previewPremises(file),
          (seen) => `строк в файле: ${seen.total}, будет внесено: ${seen.written}`,
        ),
      setPlan,
    )

  // Запись - фоновой работой по прочитанному файлу (20.10.2026); ответ тот же,
  // что отдавал запрос записи: сервер вызывает тот же обработчик.
  const load = () =>
    act(
      () => flow.write('premises', { column_overrides: null }),
      (done) => {
        setPlan(done)
        setWritten(true)
        // Чек-лист готовности считает площади: после записи он обязан
        // пересчитаться, иначе человек видит «нет» по только что внесённому.
        onLoaded()
      },
    )

  return (
    <>
      <UploadHelp
        title="Где это взять в 1С"
        where={[
          'В 1С откройте «Отчёт по помещениям» (раздел «ЖКХ», группа «Отчёты»). ' +
            'Выведите в него лицевой счёт, общую и жилую площадь, количество комнат и этаж, ' +
            'затем сохраните: «Ещё» → «Сохранить» → «Лист Excel 2007 (xlsx)» или CSV.',
          'В отчёте по начислениям этих граф нет по устройству: он строится по регистру ' +
            'взаиморасчётов, а площади и этаж лежат в справочнике помещений.',
        ]}
        sample={SAMPLE}
        note={
          'Достаточно одной графы из четырёх: пустая означает «неизвестно» и прежнего ' +
          'значения не стирает. Счёт, которого нет в портфеле, этим файлом не заводится - ' +
          'в нём нет ни адреса, ни вида помещения. Файла нет вовсе - те же сведения ' +
          'вносятся на карточке счёта, в разделе «Помещение».'
        }
      />

      <section className="section card">
        <div className="field field--l">
          <label className="field__control">
            <span className="field__label">Файл со сведениями о помещениях</span>
            <input
              key={`${round}-${flow.attempt}`}
              className="field__file"
              type="file"
              accept=".csv,.xlsx"
              disabled={busy || flow.busy}
              onChange={(event) => choose(event.target.files?.[0])}
            />
          </label>
        </div>
        <UploadStages flow={flow} />

        <div className="row">
          <button type="button" className="button" disabled={busy || !file || written} onClick={look}>
            Посмотреть, что получится
          </button>
          <button
            type="button"
            className="button"
            disabled={busy || !file || !plan || written}
            onClick={load}
          >
            Внести в портфель
          </button>
        </div>

        <Notice error={flow.error ?? error} />
        {/* Конец - блок «Готово» (20.10.2026); перечень изменений - под ним. */}
        {written ? (
          <UploadEnd
            title="Готово: сведения о помещениях внесены в портфель"
            facts={[
              ['Строк в файле', plan.total],
              ['Внесено сведений', plan.written],
              ['Уже совпадало', plan.unchanged || null],
              ['Строк, оставшихся вам', plan.issues.length],
            ]}
            seconds={flow.written?.seconds}
            onAgain={again}
          />
        ) : null}
        {plan ? <Plan plan={plan} /> : null}
      </section>
    </>
  )
}
